import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const NewsletterCard = ({
  id,
  title,
  date,
  summary,
  tags = [],
  status = 'published',
  readCount = 0,
  isAdminView = false,
  slug,
  onEdit,
  onDelete,
  onPublish,
}) => {
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString)?.toLocaleDateString('en-US', options);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-success/10 text-success border-success/20';
      case 'draft':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'scheduled':
        return 'bg-secondary/10 text-secondary border-secondary/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <article className="group bg-card border border-border rounded-lg shadow-elevation-sm hover:shadow-elevation-md transition-all duration-300 ease-smooth overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1">
            <h3 className="text-xl font-heading font-semibold text-foreground mb-2 group-hover:text-primary transition-colors duration-250">
              {isAdminView ? (
                <span className="cursor-pointer">{title}</span>
              ) : (
                <Link to={`/newsletter/${slug}`} className="hover:underline">
                  {title}
                </Link>
              )}
            </h3>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="Calendar" size={14} />
                <time dateTime={date}>{formatDate(date)}</time>
              </div>
              {readCount > 0 && (
                <div className="flex items-center gap-1">
                  <Icon name="Eye" size={14} />
                  <span>{readCount} reads</span>
                </div>
              )}
            </div>
          </div>
          {isAdminView && (
            <span
              className={`px-3 py-1 text-xs font-medium border rounded-full ${getStatusColor(
                status
              )}`}
            >
              {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
            </span>
          )}
        </div>

        <p className="text-sm text-foreground/80 leading-relaxed mb-4 line-clamp-3">
          {summary}
        </p>

        {tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tags?.map((tag, index) => (
              <span
                key={index}
                className="px-2.5 py-1 text-xs font-medium bg-muted text-muted-foreground rounded-md hover:bg-muted/80 transition-colors duration-250"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-border">
          {isAdminView ? (
            <div className="flex items-center gap-2">
              {onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(id)}
                  iconName="Edit"
                  iconPosition="left"
                  iconSize={14}
                >
                  Edit
                </Button>
              )}
              {status === 'draft' && onPublish && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => onPublish(id)}
                  iconName="Send"
                  iconPosition="left"
                  iconSize={14}
                  className="bg-success hover:bg-success/90 text-success-foreground"
                >
                  Publish
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDelete(id)}
                  iconName="Trash2"
                  iconPosition="left"
                  iconSize={14}
                >
                  Delete
                </Button>
              )}
            </div>
          ) : (
            <Link to={`/newsletter/${slug}`}>
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowRight"
                iconPosition="right"
                iconSize={14}
                className="text-white hover:text-[#FFD700] border-white/30 hover:border-[#FFD700] transition-colors duration-250"
              >
                Read More
              </Button>
            </Link>
          )}
        </div>
      </div>
    </article>
  );
};

export default NewsletterCard;